import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { trackClickMessage, trackClickRecommendation, trackOfferMessageTeaserShow } from './tracking';
import { retrySendMessageById } from '../../actions/sendMessage/retrySendMessageById';
import { OfferMessage } from '../../components/OfferMessage';
import { CHATBOT_ID } from '../../constants/chatbotId';
import { currentOpenChatIDSelector } from '../../selectors';
import { TMessage, TOutcomingStatus } from '../../types/messages';

type TOfferMessageContainerProps = {
  isOutcoming: boolean;
  isMessageFailed: boolean;
  messageId: string;
  message: TMessage & { status: TOutcomingStatus };
};

export const OfferMessageContainer: React.FC<TOfferMessageContainerProps> = ({
  isMessageFailed,
  isOutcoming,
  message,
  messageId,
}) => {
  const dispatch = useDispatch();
  const chatId = useSelector(currentOpenChatIDSelector);
  const offer = message?.offers && message?.offers[0];
  const isChatbotChat = Boolean(chatId === CHATBOT_ID);

  const handleReload = React.useCallback(() => {
    dispatch(retrySendMessageById(chatId as string, messageId));
  }, [chatId, dispatch, messageId]);

  const handleTrackOfferMessageTeaserShow = React.useCallback(() => {
    if (offer) {
      trackOfferMessageTeaserShow({
        offerId: offer.realtyOfferId,
        block: isChatbotChat ? 'Chat_cian_helper' : 'Chat_snippet',
      });
    }
  }, [isChatbotChat, offer]);

  const onClick = React.useCallback(() => {
    if (isChatbotChat && offer) {
      trackClickRecommendation(offer.realtyOfferId, message.communicationId);
    } else if (offer && chatId) {
      trackClickMessage(offer.realtyOfferId, chatId, messageId, message.communicationId);
    }
  }, [isChatbotChat, offer]);

  if (!offer) {
    return null;
  }

  const { formattedAddress, formattedInfo, formattedPrice, formattedOldPrice, url, photoUrls } = offer;

  const param = `source=${isChatbotChat ? 'chat_bot' : 'chats'}`;
  const shareUrl = url.includes('?') ? `${url}&${param}` : `${url}?${param}`;

  return (
    <ActionAfterViewObserver
      percentVisible={0.5}
      callback={handleTrackOfferMessageTeaserShow}
      triggerOnce={false}
      callbackDelay={1000}
    >
      <OfferMessage
        isOutcoming={isOutcoming}
        formattedAddress={formattedAddress}
        formattedInfo={formattedInfo}
        formattedPrice={formattedPrice}
        formattedOldPrice={formattedOldPrice}
        shareUrl={shareUrl}
        photoUrl={photoUrls[0]}
        withReload={isMessageFailed}
        onReload={handleReload}
        onClick={onClick}
      />
    </ActionAfterViewObserver>
  );
};
